import { memo, forwardRef, useImperativeHandle, useState } from 'react';

import { useDisclosure, useToast } from '@chakra-ui/react';

import { editHarvestSummaryInventoryCount } from 'api/reports';
import { useMutation, useQueryClient } from 'react-query';

import {
  EditHarvestSummaryFormModalRefType,
  EditHarvestSummaryFormModalProps,
} from './EditHarvestSummaryFormModal.props';

import EditHarvestSummaryFormModalView from './EditHarvestSummaryFormModal.view';
import { Report } from 'types/report';

const Container = forwardRef<
  EditHarvestSummaryFormModalRefType,
  EditHarvestSummaryFormModalProps
>((props, ref) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [report, setReport] = useState<Report | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useImperativeHandle(
    ref,
    (): any => ({
      onOpen: (reportData: Report) => {
        setReport(reportData);
        setValidationError(null);
        onOpen();
      },
    }),
    [onOpen],
  );

  const { mutate, isLoading } = useMutation(editHarvestSummaryInventoryCount, {
    onSuccess: () => {
      toast({
        status: 'success',
        title: 'Success',
        description: 'Harvest Summary Updated Successfully',
        isClosable: true,
        duration: 5000,
      });

      // Invalidate all related queries to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['fetch-reports'],
      });

      queryClient.invalidateQueries({
        queryKey: ['fetch-inventory-reports'],
      });

      // Also invalidate any queries that might contain the specific report
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes('fetch-reports') ||
          query.queryKey.includes('fetch-inventory-reports') ||
          query.queryKey.includes('fetch-impact-reports'),
      });

      // Force refetch the specific report data if it's cached
      if (report?._id) {
        queryClient.invalidateQueries({
          queryKey: ['fetch-reports', report._id],
        });

        // Invalidate activity history queries for this specific report
        queryClient.invalidateQueries({
          queryKey: [`fetch-report-activity-history-${report._id}`],
        });
      }

      onClose();

      // Close the parent modal if callback is provided
      if (props.onParentClose) {
        props.onParentClose();
      }
    },
    onError: (error: any) => {
      const data = error?.response?.data;
      if (
        data?.message?.includes(
          'Ending Inventory should not be less than Beginning Inventory',
        )
      ) {
        setValidationError(
          'The ending date cannot be earlier than the beginning date. Please check your date selection.',
        );
        toast({
          status: 'error',
          title: 'Invalid Date Range',
          description:
            'The ending date cannot be earlier than the beginning date. Please check your date selection.',
          isClosable: true,
          duration: 8000,
        });
      } else if (
        data?.message?.includes('Cannot select same inventory count')
      ) {
        setValidationError(
          'You cannot select the same inventory count for both beginning and ending. Please choose different inventory counts.',
        );
        toast({
          status: 'error',
          title: 'Invalid Selection',
          description:
            'You cannot select the same inventory count for both beginning and ending. Please choose different inventory counts.',
          isClosable: true,
          duration: 8000,
        });
      }
    },
  });

  return (
    <EditHarvestSummaryFormModalView
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={mutate}
      report={report}
      validationError={validationError}
    />
  );
});

const EditHarvestSummaryFormModal = memo(Container);

export default EditHarvestSummaryFormModal;
